import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import { type CreateJobPayload, type JobPlan, type JobScope, useCreateJobMutation } from '@/client/api/assets/jobs';
import { REJECT_REASONS } from '@/client/api/assets/moderation';
import { processorName } from '@/client/api/assets/signals';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BULK = [
  ['approve', 'Approve (clear from the review queue)'],
  ['hide', 'Hide (seller told, cannot restore)'],
  ['reject', 'Reject (seller told, cannot restore)'],
  ['restore', 'Restore hidden or rejected photos'],
  ['flag', 'Flag for review (internal)'],
  ['unflag', 'Clear flags'],
  ['delete', 'Delete (soft)'],
  ['undelete', 'Undelete'],
  ['measure', 'Measure (pHash + image metrics, free; feeds duplicates and local metrics)'],
  ['scan_delivery', 'Scan delivery (request every file, record what is broken; read-only)'],
  ['repair_uc', 'Repair Uploadcare copies (clear dead ones so the sync re-uploads)'],
];
const PROCESSORS = ['task:asset-local-metrics', 'task:asset-vision'];
const MODES = [
  ['missing', 'Only photos missing these signals'],
  ['stale', 'Missing, or made by an older processor version'],
  ['force', 'Every photo (re-run all)'],
  ['shadow', 'Shadow: run the deployed version into history only, to compare'],
];

const errBody = (e: any) => {
  const b = e?.data ?? e?.response?.data ?? {};
  return b?.data && typeof b.data === 'object' ? b.data : b;
};

export interface JobLauncherProps {
  open: boolean;
  onClose: () => void;
  scope: JobScope | null;
  /** e.g. "12 selected photos" or "all 729 matching the filter". */
  scopeLabel: string;
  initial?: { kind: 'bulk' | 'signal'; action?: string; processors?: string[]; mode?: string };
}

/** Start a background job over a selection or a Library filter: pick, preview
 *  (dry run), give a reason, confirm the count when it is risky, go. */
const JobLauncherDialog = ({ open, onClose, scope, scopeLabel, initial }: JobLauncherProps) => {
  const navigate = useNavigate();
  const create = useCreateJobMutation();
  const [kind, setKind] = useState<'bulk' | 'signal'>('bulk');
  const [action, setAction] = useState('approve');
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [processors, setProcessors] = useState<string[]>(['task:asset-local-metrics']);
  const [mode, setMode] = useState('missing');
  const [plan, setPlan] = useState<JobPlan | null>(null);
  const [planError, setPlanError] = useState<{ message: string; body: any } | null>(null);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    if (!open) return;
    setKind(initial?.kind ?? 'bulk');
    setAction(initial?.action ?? (initial?.kind === 'signal' ? 'run' : 'approve'));
    setProcessors(initial?.processors ?? ['task:asset-local-metrics']);
    setMode(initial?.mode ?? 'missing');
    setReasonCode('');
    setNote('');
    setReason('');
    setConfirm('');
    setPlan(null);
    setPlanError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(initial ?? null)]);  // by value: callers pass a fresh object each render

  // Any change to what would run invalidates the preview.
  useEffect(() => { setPlan(null); setPlanError(null); setConfirm(''); }, [kind, action, reasonCode, note, processors, mode]);

  const payload = (): CreateJobPayload => ({
    kind,
    action: kind === 'signal' ? 'run' : action,
    scope: scope!,
    params: kind === 'signal'
      ? { processors, mode }
      : { ...(reasonCode ? { reason_code: reasonCode } : {}), ...(note.trim() ? { note: note.trim() } : {}) },
  });

  const preview = async () => {
    try {
      setPlan(await create.mutateAsync({ ...payload(), dry_run: true }));
      setPlanError(null);
    } catch (e) {
      setPlan(null);
      setPlanError({ message: getApiErrorMessage(e, 'The job cannot run'), body: errBody(e) });
    }
  };

  const risky = !!plan && (plan.paid_calls > 0 || ['hide', 'reject', 'delete'].includes(plan.action));
  const ready = !!plan && reason.trim() && (!risky || Number(confirm) === plan.total);

  const start = async () => {
    if (!plan) return;
    try {
      const job = await create.mutateAsync({ ...payload(), reason: reason.trim(), confirm_count: risky ? plan.total : undefined });
      toast.success(`Job #${job.ajid} started: ${job.total} photos`);
      onClose();
      navigate(`/admin/assets/bulk?job=${job.ajid}`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'The job did not start'));
    }
  };

  const needsReason = kind === 'bulk' && (action === 'hide' || action === 'reject');
  const needsNote = kind === 'bulk' && (action === 'flag' || reasonCode === 'other');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Run a job</DialogTitle>
          <DialogDescription>On {scopeLabel}. Runs in the background; you can cancel it, and moderation jobs can be reverted for 30 days.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="flex gap-2">
            {(['bulk', 'signal'] as const).map((k) => (
              <button key={k} onClick={() => setKind(k)}
                className={`rounded-full border px-3 py-1 ${kind === k ? 'border-gray-900 bg-gray-900 text-white' : 'bg-white'}`}>
                {k === 'bulk' ? 'Moderate or delete' : 'Run analysis'}
              </button>
            ))}
          </div>

          {kind === 'bulk' ? (
            <>
              <select aria-label="Action" className="w-full rounded-md border border-input bg-background px-3 py-2"
                value={action} onChange={(e) => setAction(e.target.value)}>
                {BULK.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {needsReason && (
                <select aria-label="Reason the seller sees" className="w-full rounded-md border border-input bg-background px-3 py-2"
                  value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
                  <option value="">Reason the seller will see…</option>
                  {Object.entries(REJECT_REASONS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )}
              {(needsNote || needsReason || action === 'restore' || action === 'approve') && (
                <Input placeholder={needsNote ? 'Note (required)' : 'Note (optional)'} value={note} onChange={(e) => setNote(e.target.value)} />
              )}
            </>
          ) : (
            <>
              <div className="space-y-1">
                {PROCESSORS.map((p) => (
                  <label key={p} className="flex items-center gap-2">
                    <input type="checkbox" checked={processors.includes(p)}
                      onChange={(e) => setProcessors((s) => (e.target.checked ? [...s, p] : s.filter((x) => x !== p)))} />
                    {processorName(p)} {p === 'task:asset-vision' && <span className="text-xs text-purple-700">paid per image</span>}
                  </label>
                ))}
              </div>
              <select aria-label="Which photos" className="w-full rounded-md border border-input bg-background px-3 py-2"
                value={mode} onChange={(e) => setMode(e.target.value)}>
                {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </>
          )}

          <Button variant="outline" className="w-full" disabled={!scope || create.isPending || (kind === 'signal' && !processors.length)
            || (needsReason && !reasonCode) || (needsNote && !note.trim())} onClick={() => void preview()}>
            {create.isPending && !plan ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Preview (dry run)
          </Button>

          {planError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-700">
              {planError.message}
              {planError.body?.live_sample?.length > 0 && <div className="mt-1 text-xs">e.g. #{planError.body.live_sample.slice(0, 6).join(', #')}</div>}
            </div>
          )}

          {plan && (
            <div className="space-y-1 rounded-md border bg-gray-50 p-3">
              <div><b>{plan.total.toLocaleString()}</b> photos will be changed{plan.skipped ? `, ${plan.skipped.toLocaleString()} skipped (up to date or not analysable)` : ''}.</div>
              <div className="text-gray-600">{plan.listings.toLocaleString()} listings{plan.seller_notices ? ` · ${plan.seller_notices.toLocaleString()} seller notices` : ''}</div>
              {plan.steps && <div className="text-gray-600">{Object.entries(plan.steps).map(([p, n]) => `${processorName(p)}: ${n}`).join(' · ')}</div>}
              {plan.paid_calls > 0 && (
                <div className="text-purple-800">
                  {plan.paid_calls.toLocaleString()} billed vision calls
                  {plan.cost_estimate_usd != null ? ` ≈ $${plan.cost_estimate_usd}` : ' (price not configured)'} · today {plan.paid_used_today}/{plan.paid_cap}
                </div>
              )}
              {plan.env && plan.env !== 'prod' && <div className="text-amber-700">Started from {plan.env}: it acts on the shared production database.</div>}
            </div>
          )}

          {plan && (
            <>
              <Input placeholder="Why are you running this? (required, audit log)" value={reason} onChange={(e) => setReason(e.target.value)} />
              {risky && (
                <Input inputMode="numeric" placeholder={`Type ${plan.total} to confirm`} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!ready || create.isPending} className={risky ? 'bg-red-600 hover:bg-red-700' : undefined} onClick={() => void start()}>
            Start job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JobLauncherDialog;
